import { Checkbox } from '@components/atoms/Checkbox/Checkbox';
import type { NewsletterSignupFieldsFragment } from '@src/lib/graphql/generated';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import { useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { defaultNewsletterCard, newsletterPlaceholderImages } from './newsletterContent';

export type NewsletterTopic = NewsletterSignupFieldsFragment & { sending_frequency?: string; preview_url?: string };
export const newsletterDisplayTitle = (title?: string) => title?.replace(/^TVM\s*\|\s*/i, '') ?? '';

export const NewsletterTopicCard = ({
  newsletter,
  checked,
  onChange,
  compact = false,
}: {
  newsletter: NewsletterTopic;
  checked: boolean;
  onChange: () => void;
  compact?: boolean;
}) => {
  const id = `newsletter-${useId()}-${newsletter.id}`;
  const title = newsletterDisplayTitle(newsletter.title);
  const imageUrl = newsletter.image?.url?.trim();
  const placeholderImage = newsletterPlaceholderImages[newsletter.slug] || defaultNewsletterCard.image;
  const sendingFrequency =
    newsletter.sending_frequency?.trim() ||
    newsletter.preferences_tagline?.trim() ||
    defaultNewsletterCard.sending_frequency;
  const previewUrl =
    newsletter.preview_url && /^https?:\/\//i.test(newsletter.preview_url) ? newsletter.preview_url : null;
  if (compact) {
    return (
      <article
        className={twMerge(
          'flex h-full flex-col gap-4 rounded-lg border-0.5 border-surface/60 bg-surface/70 p-4 backdrop-blur-sm transition-colors',
          checked && 'border-primary',
        )}
      >
        <Checkbox
          id={id}
          label={title}
          checkboxProps={{ checked, onChange }}
          classProps={{ label: 'ml-3 font-herokid text-base font-medium text-foreground', root: 'items-start' }}
        />
        <p className="label-sm text-foreground/60">{sendingFrequency}</p>
        <div
          className="prose prose-sm line-clamp-3 text-foreground/60 leading-body"
          dangerouslySetInnerHTML={{ __html: newsletter.description || newsletter.preferences_text || '' }}
        />
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="label-sm mt-auto self-end text-foreground hover:text-primary"
            aria-label={`${title}: letzten Newsletter ansehen`}
          >
            Vorschau ›
          </a>
        )}
      </article>
    );
  }
  const heading = (
    <h3 id={`${id}-title`} className="font-herokid font-semibold text-2xl text-foreground leading-tight">
      {title}
    </h3>
  );
  return (
    <article
      aria-labelledby={`${id}-title`}
      className={twMerge(
        'flex h-full min-h-[420px] flex-col gap-4 rounded-sm border border-surface/60 bg-surface/50 p-5 backdrop-blur-lg transition-colors',
        checked && 'border-primary',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="relative h-28 w-28 shrink-0 overflow-hidden sm:h-32 sm:w-32">
          <Image
            src={imageUrl || placeholderImage}
            alt={imageUrl ? newsletter.image?.alt || '' : ''}
            fill
            sizes="(min-width:478px) 256px, 224px"
            copyright={imageUrl ? newsletter.image?.copyright_text || '' : ''}
            focus={imageUrl ? newsletter.image?.focal_point : undefined}
            classProps={{ container: 'absolute inset-0', root: 'object-cover' }}
          />
        </div>
        <label htmlFor={id} className="relative block h-11 w-11 shrink-0">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            aria-labelledby={`${id}-title`}
            className="peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          />
          <span
            className={twMerge(
              'pointer-events-none flex h-full w-full items-center justify-center rounded-full border-2 border-foreground/80 text-foreground transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-4 peer-focus-visible:ring-offset-background peer-disabled:opacity-40',
              checked && 'border-primary bg-primary text-white',
            )}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M-8 0H8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                className="origin-top-left transition-transform duration-300 ease-in-out motion-reduce:transition-none"
                style={{
                  transform: checked
                    ? 'translate(7px, 14px) rotate(45deg) scaleX(0.3536)'
                    : 'translate(12px, 12px) rotate(90deg) scaleX(1)',
                }}
              />
              <path
                d="M-8 0H8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                className="origin-top-left transition-transform duration-300 ease-in-out motion-reduce:transition-none"
                style={{
                  transform: checked
                    ? 'translate(14px, 11px) rotate(-45deg) scaleX(0.8839)'
                    : 'translate(12px, 12px) rotate(0deg) scaleX(1)',
                }}
              />
            </svg>
          </span>
        </label>
      </div>
      <p className="label-base pt-1 text-foreground/60">{sendingFrequency}</p>
      {heading}
      <div
        className="prose prose-sm line-clamp-5 text-foreground/70 leading-body"
        dangerouslySetInnerHTML={{ __html: newsletter.description || newsletter.preferences_text || '' }}
      />
      {previewUrl && (
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto self-start pt-2 font-medium text-foreground text-sm underline underline-offset-4 hover:text-primary"
          aria-label={`${title}: letzten Newsletter ansehen`}
        >
          Letzten Newsletter ansehen
        </a>
      )}
    </article>
  );
};
